import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  RefreshCw, Download, Filter, X, 
  Package, Truck, User, MapPin, Calendar,
  Clock, CheckCircle2, AlertCircle
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'received', label: 'Received (Store)' },
  { value: 'processed', label: 'Processed (GRN)' }
];

const NATURE_OPTIONS = [
  { value: 'all', label: 'All Natures' },
  { value: 'Purchase-RMT/FNG', label: 'Purchase - RMT/FNG' },
  { value: 'Purchase-CTM', label: 'Purchase - CTM' },
  { value: 'Jobwork-Own', label: 'Jobwork - Own' },
  { value: 'Jobwork-Client', label: 'Jobwork - Client' },
  { value: 'Sales Return', label: 'Sales Return' }
];

export default function GateEntryRegisterPage() {
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [locations, setLocations] = useState([]);
  const [securityPersons, setSecurityPersons] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    status: 'all',
    nature: 'all',
    location_id: 'all',
    security_person_id: 'all',
    date_from: '',
    date_to: '',
    search: ''
  });
  
  const [showFilters, setShowFilters] = useState(true);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Fetch locations
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axios.get(`${API_URL}/locations`, { headers: getAuthHeader() });
        setLocations(response.data || []);
      } catch (error) {
        console.error('Error fetching locations:', error);
      }
    };
    fetchLocations();
  }, []);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.nature && filters.nature !== 'all') params.append('nature', filters.nature);
      if (filters.location_id && filters.location_id !== 'all') params.append('location_id', filters.location_id);
      if (filters.security_person_id && filters.security_person_id !== 'all') params.append('security_person_id', filters.security_person_id);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.search) params.append('search', filters.search);
      
      const response = await axios.get(
        `${API_URL}/store/gate-entries/register?${params.toString()}`,
        { headers: getAuthHeader() }
      );
      
      setEntries(response.data.entries || []);
      setTotal(response.data.total || 0);
      setSecurityPersons(response.data.security_persons || []);
    } catch (error) {
      console.error('Error fetching entries:', error);
      toast.error('Failed to load gate entries');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const clearFilters = () => {
    setFilters({
      status: 'all',
      nature: 'all',
      location_id: 'all',
      security_person_id: 'all',
      date_from: '',
      date_to: '',
      search: ''
    });
  };

  const openEntryDetails = (entry) => {
    setSelectedEntry(entry);
    setDetailsOpen(true);
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    return `${formatDate(isoString)} ${formatTime(isoString)}`;
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      received: 'bg-blue-100 text-blue-800 border-blue-300',
      processed: 'bg-green-100 text-green-800 border-green-300'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Gate Entry Register</h1>
            <p className="text-muted-foreground">View all gate entries across locations</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}>
              <Filter className="w-4 h-4 mr-1" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
            <Button variant="outline" size="sm" onClick={fetchEntries}>
              <RefreshCw className="w-4 h-4 mr-1" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <Card>
            <CardHeader className="py-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">Filters</CardTitle>
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="w-4 h-4 mr-1" />
                  Clear All
                </Button>
              </div>
            </CardHeader>
            <CardContent className="py-3">
              <div className="grid grid-cols-7 gap-4">
                {/* Search */}
                <div>
                  <Label className="text-xs">Search</Label>
                  <Input
                    placeholder="Entry no, supplier..."
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    className="h-9"
                  />
                </div>

                {/* Status */}
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select
                    value={filters.status}
                    onValueChange={(v) => setFilters(prev => ({ ...prev, status: v }))}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Location */}
                <div>
                  <Label className="text-xs">Location/Gate</Label>
                  <Select
                    value={filters.location_id}
                    onValueChange={(v) => setFilters(prev => ({ ...prev, location_id: v }))}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="All Locations" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Locations</SelectItem>
                      {locations.map(loc => (
                        <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Nature */}
                <div>
                  <Label className="text-xs">Nature</Label>
                  <Select
                    value={filters.nature}
                    onValueChange={(v) => setFilters(prev => ({ ...prev, nature: v }))}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="All Natures" />
                    </SelectTrigger>
                    <SelectContent>
                      {NATURE_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date From */}
                <div>
                  <Label className="text-xs">Date From</Label>
                  <Input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
                    className="h-9"
                  />
                </div>

                {/* Date To */}
                <div>
                  <Label className="text-xs">Date To</Label>
                  <Input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
                    className="h-9"
                  />
                </div>

                {/* Security Person */}
                <div>
                  <Label className="text-xs">Security Person</Label>
                  <Select
                    value={filters.security_person_id}
                    onValueChange={(v) => setFilters(prev => ({ ...prev, security_person_id: v }))}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="All Security" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Security</SelectItem>
                      {securityPersons.map(sp => (
                        <SelectItem key={sp.id} value={sp.id}>{sp.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Entries Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin mr-2" />
                <span>Loading entries...</span>
              </div>
            ) : entries.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No gate entries found matching your filters</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[120px]">Entry No</TableHead>
                      <TableHead className="w-[100px]">Date</TableHead>
                      <TableHead className="w-[80px]">Time</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead className="w-[100px]">Invoice/DC</TableHead>
                      <TableHead className="w-[100px]">Nature</TableHead>
                      <TableHead className="w-[100px]">Location</TableHead>
                      <TableHead className="w-[120px]">Security</TableHead>
                      <TableHead className="w-[80px]">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.map((entry) => (
                      <TableRow 
                        key={entry.id} 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => openEntryDetails(entry)}
                      >
                        <TableCell className="font-mono text-xs">{entry.gate_entry_no}</TableCell>
                        <TableCell className="text-xs">{formatDate(entry.created_at)}</TableCell>
                        <TableCell className="text-xs">{formatTime(entry.created_at)}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{entry.supplier_name_raw}</TableCell>
                        <TableCell className="text-xs">{entry.invoice_dc_no || '-'}</TableCell>
                        <TableCell className="text-xs">{entry.nature_of_inward?.replace('-', ' - ')}</TableCell>
                        <TableCell className="text-xs">{entry.unit_location_name || entry.location_code || '-'}</TableCell>
                        <TableCell className="text-xs">{entry.security_person_name || '-'}</TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${getStatusBadge(entry.status)}`}>
                            {entry.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Showing count */}
        {!loading && entries.length > 0 && (
          <p className="text-sm text-muted-foreground text-center">
            Showing {entries.length} of {total} entries
          </p>
        )}
      </div>

      {/* Entry Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Gate Entry Details</DialogTitle>
            <DialogDescription>{selectedEntry?.gate_entry_no}</DialogDescription>
          </DialogHeader>
          
          {selectedEntry && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Supplier</Label>
                  <p className="font-medium">{selectedEntry.supplier_name_raw}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  <Badge className={getStatusBadge(selectedEntry.status)}>{selectedEntry.status}</Badge>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Invoice/DC No</Label>
                  <p className="text-sm">{selectedEntry.invoice_dc_no || '-'}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Nature of Inward</Label>
                  <p className="text-sm">{selectedEntry.nature_of_inward}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Location</Label>
                  <p className="text-sm">{selectedEntry.unit_location_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Created At</Label>
                  <p className="text-sm">{formatDateTime(selectedEntry.created_at)}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Security Person</Label>
                  <p className="text-sm">{selectedEntry.security_person_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Vehicle No</Label>
                  <p className="text-sm">{selectedEntry.vehicle_no || '-'}</p>
                </div>
              </div>
              
              {selectedEntry.items && selectedEntry.items.length > 0 && (
                <div className="pt-4 border-t">
                  <Label className="text-xs text-muted-foreground">Items ({selectedEntry.items.length})</Label>
                  <div className="mt-2 space-y-1">
                    {selectedEntry.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm bg-muted/50 p-2 rounded">
                        <span>{item.item_name}</span>
                        <span className="text-muted-foreground">
                          Qty: {item.quantity} | Pkgs: {item.num_packages || 1}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedEntry.remarks && (
                <div className="pt-4 border-t">
                  <Label className="text-xs text-muted-foreground">Remarks</Label>
                  <p className="text-sm">{selectedEntry.remarks}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
